import { useEffect, useState, type InputHTMLAttributes } from "react";
import {
  formatUsPhoneNumber,
  isValidUsPhoneNumber,
  normalizeUsPhoneNumber,
  toUsE164Phone,
} from "@constructorfleet/utils";
import { TextInput } from "./text-input";

export { formatUsPhoneNumber, normalizeUsPhoneNumber, toUsE164Phone };

type PhoneInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "inputMode" | "value" | "onChange"
> & {
  value?: string;
  onChange?: (value: string) => void;
  valueFormat?: "national" | "e164";
  validateOnChange?: boolean;
  onValidation?: (isValid: boolean) => void;
};

export function PhoneInput({
  value = "",
  onChange,
  valueFormat = "national",
  validateOnChange = false,
  onValidation,
  ...props
}: PhoneInputProps) {
  const [touched, setTouched] = useState(false);
  const [inputValue, setInputValue] = useState(normalizeUsPhoneNumber(String(value)));
  useEffect(() => {
    setInputValue(normalizeUsPhoneNumber(String(value)));
  }, [value]);
  const normalizedValue = normalizeUsPhoneNumber(inputValue);
  const validation = isValidUsPhoneNumber(normalizedValue);
  const showInvalid =
    touched && validateOnChange && normalizedValue.trim().length > 0 && !validation.ok;

  return (
    <TextInput
      {...props}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={formatUsPhoneNumber(normalizedValue)}
      aria-invalid={showInvalid || undefined}
      data-invalid={showInvalid || undefined}
      onChange={(event) => {
        setTouched(true);
        const inputValue = event.target.value;
        if (props.required === true && inputValue.trim().length === 0) {
          setInputValue("");
          onValidation?.(false);
          onChange?.("");
          return;
        }
        const nextValue = normalizeUsPhoneNumber(inputValue);
        setInputValue(nextValue);
        onChange?.(valueFormat === "e164" ? toUsE164Phone(nextValue) : nextValue);
        onValidation?.(isValidUsPhoneNumber(nextValue).ok);
      }}
    />
  );
}
